<script lang="ts">
  import type { DealerTheme } from "conversion_flows/shared/contexts/dealerTheme"
  import themeTokens from "conversion_flows/theme/LibUIThemeTokens"
  import { getContext } from "svelte"
  import type { Action } from "svelte/action"
  import Svg from "website_script/components/Svg.svelte"

  type Size = "sm" | "md" | "lg"
  /**
   * Color variants here move away from the equivalent in `@autouncle/ui`.
   * They instead match the color variants of the `AUButton`, for better
   * compatibility between the 2 (very similar) components.
   */
  type Color = "primary" | "secondary"

  export let sroText: string
  export let size: Size = "md"
  export let icon: string
  export let color: Color = "secondary"
  export let onClick: (e: KeyboardEvent | MouseEvent) => void

  export let use: Action<HTMLElement, any>[] = []

  const combinedActions = (node: HTMLElement) => {
    const destructors = use.map((action) => {
      return action(node)
    })

    return {
      destroy() {
        destructors.forEach((destructor) => {
          if (typeof destructor?.destroy == "function") destructor.destroy()
        })
      },
    }
  }

  const dealerTheme = getContext<DealerTheme>("dealerTheme")

  const tokens = themeTokens(dealerTheme)

  const svgColor = (color: Color) => {
    switch (color) {
      case "secondary":
        return tokens("colors.primaryForegroundColor")
      case "primary":
      default:
        return tokens("colors.primaryContrastForeground")
    }
  }
</script>

<span
  class="au-button-icon"
  tabindex="0"
  role="button"
  data-size={size}
  data-color={color}
  title={sroText}
  {...$$restProps}
  on:click={onClick}
  on:focus
  on:blur
  on:mouseover
  on:mouseenter
  on:mouseleave
  on:keydown={(event) => {
    if (event.key === " " || event.key === "Enter") {
      onClick(event)
    }
  }}
  use:combinedActions
>
  <span class="au-sr-only">{sroText}</span>
  <Svg data={icon} color={String(svgColor(color))} aria-hidden="true" />
</span>

<style>
  .au-button-icon {
    --au-button-border-radius: 100%;

    border-radius: var(--au-button-border-radius);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all var(--au-transition-fast);
    padding: var(--au-button-padding);
    cursor: pointer;

    /* Color props */
    background-color: var(--au-button-background-color);
  }

  .au-button-icon:hover {
    background-color: var(--au-button-hover-background-color);
  }

  .au-button-icon:active {
    background-color: var(--au-button-active-background-color);
  }

  .au-button-icon:focus {
    outline: var(--au-focus-outline-width) solid var(--au-focus-outline-color);
    outline-offset: calc(-1 * var(--au-focus-outline-width));
  }

  .au-button-icon[disabled] {
    opacity: var(--au-disabled-opacity);
    cursor: not-allowed;
    pointer-events: none;
  }

  .au-button-icon[data-size="sm"] {
    --au-button-padding: var(--au-spacing-2xs);
  }

  .au-button-icon[data-size="sm"] :global(svg) {
    width: var(--au-spacing-md);
    height: var(--au-spacing-md);
  }

  .au-button-icon[data-size="md"] {
    --au-button-padding: var(--au-spacing-xs);
  }

  .au-button-icon[data-size="md"] :global(svg) {
    width: var(--au-spacing-2md);
    height: var(--au-spacing-2md);
  }

  .au-button-icon[data-size="lg"] {
    --au-button-padding: var(--au-spacing-xs);
  }

  .au-button-icon[data-size="lg"] :global(svg) {
    width: var(--au-spacing-lg);
    height: var(--au-spacing-lg);
  }

  .au-button-icon[data-color="primary"] {
    --au-button-background-color: var(--au-button-primary-background-color);
    --au-button-hover-background-color: var(
      --au-button-primary-hover-background-color
    );
    --au-button-active-background-color: var(
      --au-button-primary-active-background-color
    );
    --au-button-icon-color: var(--au-button-primary-icon-color);
    --au-focus-outline-color: var(--au-button-primary-icon-color);
  }

  .au-button-icon[data-color="secondary"] {
    --au-button-background-color: var(--au-button-secondary-background-color);
    --au-button-hover-background-color: var(
      --au-button-icon-secondary-hover-background-color
    );
    --au-button-active-background-color: var(
      --au-button-secondary-active-background-color
    );
    --au-button-icon-color: var(--au-button-secondary-icon-color);
    --au-focus-outline-color: var(--au-button-secondary-icon-color);
  }
</style>
